import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/alertContext';
import { getUserData } from 'helpers';
import { getAllPermission } from 'helpers';
import BackButton from 'components/UI/BackButton';
import EmptyContent from 'components/EmptyContent';
import { Person } from '@mui/icons-material';
import {
  getWorkshopEvaluationDataEmployees,
  updateWorkshopEvaluationData,
  IWorkshopEvaluationDataEmployee,
  IWorkshopEvaluationDataAnswer,
  IWorkshopEvaluationDataOption,
  IUpdateWorkshopEvaluationDataRequest,
} from 'framework/workshopEvaluationData';
import { Feature } from 'models/api';

interface Props {
  feature: Feature;
}

const WorkshopEvaluationData: React.FC<Props> = ({ feature }) => {
  const urlParams = useParams();
  const location = useLocation();
  // Extract workshopId from URL path - fallback to pathname extraction if param doesn't exist
  let workshopId: string = urlParams[`${feature.singleName}Id`] as string;
  if (!workshopId) {
    // Extract from pathname like /workshop-evaluation-data/{id}
    const pathParts = location.pathname.split('/');
    workshopId = pathParts[pathParts.length - 1];
  }
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setAlert } = useAlert();
  const userData = getUserData();
  const { colors } = userData;

  // State
  const [loading, setLoading] = useState<boolean>(true);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [employeeEvaluations, setEmployeeEvaluations] = useState<IWorkshopEvaluationDataEmployee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const permissions = await getAllPermission();
        const evaluationDataPermission = permissions?.find(
          (p) => p.permissionName === 'WorkshopEvaluationData'
        );
        const hasAccess = 
          evaluationDataPermission?.hasReadPermission || 
          evaluationDataPermission?.hasModifyPermission || 
          false;
        
        setHasPermission(hasAccess);
        
        if (!hasAccess) {
          setAlert({
            open: true,
            message: 'You do not have permission to access this page',
            type: 'error',
          });
          navigate('/workshops');
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        setAlert({
          open: true,
          message: 'Error checking permissions',
          type: 'error',
        });
        navigate('/workshops');
      }
    };

    checkPermissions();
  }, [navigate, setAlert]);

  // Fetch employee evaluations
  useEffect(() => {
    if (hasPermission && workshopId) {
      fetchEmployeeEvaluations();
    }
  }, [hasPermission, workshopId]);

  const fetchEmployeeEvaluations = async () => {
    try {
      setLoadingEmployees(true);
      const response = await getWorkshopEvaluationDataEmployees(workshopId!);
      setEmployeeEvaluations(response);
    } catch (error) {
      console.error('Error fetching employee evaluations:', error);
      setAlert({
        open: true,
        message: 'Error fetching employee evaluations',
        type: 'error',
      });
    } finally {
      setLoadingEmployees(false);
      setLoading(false);
    }
  };

  const handleAnswerChange = (
    employeeId: string,
    questionId: string,
    questionType: string,
    newValue: string,
    isOptionId: boolean = false
  ) => {
    setEmployeeEvaluations(prev => 
      prev.map(emp => 
        emp.employeeId === employeeId
          ? {
              ...emp,
              answers: emp.answers.map(answer =>
                answer.questionId === questionId
                  ? {
                      ...answer,
                      selectedOptionId: isOptionId ? newValue : answer.selectedOptionId,
                      answerText: !isOptionId ? newValue : answer.answerText,
                      selectedAnswer: isOptionId 
                        ? answer.options.find(opt => opt.id === newValue)?.description || ''
                        : newValue,
                      selectedAnswerAr: isOptionId
                        ? answer.options.find(opt => opt.id === newValue)?.descriptionAr || ''
                        : newValue,
                    }
                  : answer
              ),
            }
          : emp
      )
    );
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      const updateData: IUpdateWorkshopEvaluationDataRequest = {
        workshopId: workshopId!,
        employeeAnswers: employeeEvaluations.map(emp => ({
          employeeId: emp.employeeId,
          answers: emp.answers.map(answer => ({
            questionId: answer.questionId,
            selectedOptionId: answer.questionType === 'MCQ' ? answer.selectedOptionId : undefined,
            answerText: answer.questionType === 'OpenText' ? answer.answerText : undefined,
          })),
        })),
      };
      
      await updateWorkshopEvaluationData(updateData);
      
      setAlert({
        open: true,
        message: 'Evaluation answers updated successfully',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Error saving changes:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error saving changes';
      console.error('Error details:', error?.response?.data);
      setAlert({
        open: true,
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const getDisplayText = (english: string, arabic: string) => {
    return i18n.language === 'ar' ? arabic : english;
  };

  const getOptionLetter = (index: number): string => {
    return String.fromCharCode(65 + index); // A, B, C, etc.
  };

  const getAnswerWithLetter = (answer: IWorkshopEvaluationDataAnswer): string => {
    if (answer.questionType === 'OpenText') {
      return answer.answerText || '-';
    }
    // For MCQ, find the option and add letter prefix
    const optionIndex = answer.options.findIndex(opt => 
      opt.description === answer.selectedAnswer || opt.descriptionAr === answer.selectedAnswerAr
    );
    if (optionIndex >= 0 && answer.selectedAnswer) {
      const displayText = getDisplayText(answer.selectedAnswer, answer.selectedAnswerAr || '');
      return `${getOptionLetter(optionIndex)}. ${displayText}`;
    }
    return answer.selectedAnswer || '-';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!hasPermission) {
    return (
      <Alert severity="error">
        You do not have permission to access this page.
      </Alert>
    );
  }

  return (
    <Box p={2}>
      <BackButton path={`/workshops/${workshopId}`} text={t("T_back_to_workshop_details")} />
      
      <Typography
        variant="h3"
        color={colors?.labelColor || "primary.main"}
        sx={{ mb: 3 }}
      >
        Workshop Evaluation Data
      </Typography>

      {/* Employee Evaluation Grid */}
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              Employee Evaluation Results
            </Typography>
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              disabled={saving || employeeEvaluations.length === 0}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>

          {loadingEmployees ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : employeeEvaluations.length > 0 ? (
            <TableContainer 
              component={Paper}
              sx={{
                maxWidth: '100%',
                overflowX: 'hidden',
                overflowY: 'auto'
              }}
            >
              <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '15%' }}>Employee Name</TableCell>
                    <TableCell sx={{ width: '25%' }}>Question</TableCell>
                    <TableCell sx={{ width: '15%' }}>Question Type</TableCell>
                    <TableCell sx={{ width: '15%' }}>Current Answer</TableCell>
                    <TableCell sx={{ width: '30%' }}>Change Answer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employeeEvaluations.map((employee) =>
                    employee.answers.map((answer, index) => (
                      <TableRow key={`${employee.employeeId}-${answer.questionId}`}>
                        {index === 0 && (
                          <TableCell 
                            rowSpan={employee.answers.length}
                            sx={{ 
                              width: '15%',
                              verticalAlign: 'top',
                              fontWeight: 'bold',
                              backgroundColor: 'grey.50',
                              wordWrap: 'break-word'
                            }}
                          >
                            {getDisplayText(employee.employeeName, employee.employeeNameAr)}
                          </TableCell>
                        )}
                        <TableCell sx={{ width: '25%', wordWrap: 'break-word' }}>
                          <strong>{answer.questionOrder || (index + 1)}.</strong> {getDisplayText(answer.questionTitle, answer.questionTitleAr)}
                        </TableCell>
                        <TableCell sx={{ width: '15%' }}>
                          <Chip
                            label={answer.questionType}
                            color={answer.questionType === 'MCQ' ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ width: '15%', wordWrap: 'break-word' }}>
                          {getAnswerWithLetter(answer)}
                        </TableCell>
                        <TableCell sx={{ width: '30%' }}>
                          {answer.questionType === 'MCQ' ? (
                            <Select
                              size="small"
                              fullWidth
                              value={answer.selectedOptionId || answer.options[0]?.id || ''}
                              onChange={(e) => handleAnswerChange(
                                employee.employeeId,
                                answer.questionId,
                                answer.questionType,
                                e.target.value,
                                true
                              )}
                            >
                              {answer.options.map((option, optIndex) => (
                                <MenuItem key={option.id} value={option.id}>
                                  <strong>{getOptionLetter(optIndex)}.</strong> {getDisplayText(option.description, option.descriptionAr)}
                                </MenuItem>
                              ))}
                            </Select>
                          ) : (
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              rows={3}
                              value={answer.answerText || ''}
                              onChange={(e) => handleAnswerChange(
                                employee.employeeId,
                                answer.questionId,
                                answer.questionType,
                                e.target.value,
                                false
                              )}
                              placeholder="Enter answer..."
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyContent
              icon={<Person sx={{ fontSize: "3.5rem" }} />}
              header="No Employee Evaluations"
              message="No employees have completed this evaluation yet."
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WorkshopEvaluationData;

